import { Component, OnInit, Input, Output, EventEmitter, ChangeDetectionStrategy} from '@angular/core';
import { AppService } from '@irb/app.service';
import { IPortfolioCustomLabel, IPortfolioDetail } from '@irb/irb-editors/irb-editors.model';
import { IrbDialogsEvents } from '@irb/irb-dialogs/irb-dialogs.events';
import { IrbEditorsService } from '@irb/irb-editors/irb-editors.service';
import { IGlobalSettings } from "@irb/irb-settings/irb-settings.model";
import { UtilityService } from '@irb/irb-common/services/utility.service';
import { IUserDetails } from '@irb/app.model';
import { IEventSubscriber, IEvent, IrbAppEvents } from '@irb-common/events';
import { Subscription } from 'rxjs';

@Component({ standalone: false,
  selector: 'irb-com-custom-labels',
  templateUrl: './com-custom-labels.component.html',
  styleUrls: ['./com-custom-labels.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager
})
export class ComCustomLabelsComponent implements OnInit, IEventSubscriber {
  @Input() data: IPortfolioDetail;
  @Input() globalSettings: IGlobalSettings;
  @Input('multipleporfolios') multipleporfolios: boolean = false;
  @Input() selectedPortfolioCount: number;
  @Output() change = new EventEmitter<boolean>();
  availableLabels: Array<IPortfolioCustomLabel>;
  currentLabels: Array<IPortfolioCustomLabel>;
  addedLabels: Array<IPortfolioCustomLabel>;
  removedLabels: Array<IPortfolioCustomLabel>;
  customCategory: string;
  userDetails: any;
  showPanelOverlay: boolean = false;
  private isPortfolioAccountService: boolean = false;
  eventSubscription: Subscription;

  constructor(private as: AppService, private es: IrbEditorsService, private util: UtilityService) {
    //this.isPortfolioAccountService = this.as.getItem('IRB_APP_CONFIG')?.featureFlags.ENABLE_PORT_ACC_APIS || false;
  }
  
  
  ngOnInit(): void {
    this.es.getLabels().subscribe((data: any) => {
      this.availableLabels = this.getResponse(data);
    }, () => {
      this.availableLabels = [];
    });

    this.customCategory = this.globalSettings.portfolioCustLabelAbbr;
    this.currentLabels = (this.data.labels && !this.multipleporfolios) ? [].concat(this.data.labels) : [];
    this.addedLabels = [];
    this.removedLabels = [];

    this.util.sortByKey(this.currentLabels, "name", "asc");
    
    this.userDetails = this.as.getItem("IRB_APP_CONFIG");
    let multiPortfolioAllowedLimit = (this.userDetails?.multiPortfolioAllowedLimit !== null) ? this.userDetails?.multiPortfolioAllowedLimit : 1000;
    this.showPanelOverlay = this.multipleporfolios && (this.selectedPortfolioCount > multiPortfolioAllowedLimit);

    this.eventSubscription = this.as.getEvents().subscribe((s) => {
      this.processEvent(s);
    });
  }

  processEvent(event: IEvent) {
      switch (event.type) {
          case IrbAppEvents.GRID_REFRESH:
              this.es.getLabels().subscribe((data: any) => {
                this.availableLabels = this.getResponse(data);
              }, () => {
                this.availableLabels = [];
              });
              break;
      }
  }

  showAddLabelsDialog() {
    let unavailableLabels = (this.data.labels) ? this.data.labels.filter((label: IPortfolioCustomLabel) => label.isUiDeleted || !label.isNew) : [];
    let unavailableIds = unavailableLabels.map((label: IPortfolioCustomLabel) => label.id);

    this.as.fireEvent(IrbDialogsEvents.SHOW_PORTFOLIO_CUSTOM_LABELS_MODIFY, {
      mode: (!this.multipleporfolios) ? "SINGLE_EDIT_ADD" : "MULTIPLE_EDIT_ADD",
      data: this.availableLabels.filter((label: IPortfolioCustomLabel) => !unavailableIds.includes(label.id)),
      selection: (this.data.labels) ? this.data.labels : [],
      finishButtonCallback: this.addLabels.bind(this)
    });
  }

  addLabels(out: Array<IPortfolioCustomLabel>) {
    this.data.labels = (this.data.labels) ? this.data.labels.filter((label: IPortfolioCustomLabel) => !label.isNew) : [];
    let existingIds: Array<string> = this.data.labels.map((x: IPortfolioCustomLabel) => x.id);
    out = out.filter((label: IPortfolioCustomLabel) => !existingIds.includes(label.id));

    out.forEach((label: IPortfolioCustomLabel) => {
      label.isNew = true;
      label.isUiDeleted = false;
    });

    this.data.labels = (this.data.labels) ? this.data.labels.concat(out) : [].concat(out);
    this.addedLabels = this.data.labels.filter((label: IPortfolioCustomLabel) => label.isNew);

    this.util.sortByKey(this.addedLabels, "name", "asc");
    this.change.emit(true); 
  }

  showRemoveLabelsDialog() {
    let unavailableLabels = (this.data.labels) ? this.data.labels.filter((label: IPortfolioCustomLabel) => label.isNew) : [];
    let unavailableIds = unavailableLabels.map((label: IPortfolioCustomLabel) => label.id);
    this.data.labels = (this.data.labels) ? this.data.labels : [];

    this.as.fireEvent(IrbDialogsEvents.SHOW_PORTFOLIO_CUSTOM_LABELS_MODIFY, {
      mode: (!this.multipleporfolios) ? "SINGLE_EDIT_REMOVE" : "MULTIPLE_EDIT_REMOVE",
      data: (!this.multipleporfolios) ? this.data.labels : this.availableLabels.filter((label: IPortfolioCustomLabel) => !unavailableIds.includes(label.id)),
      selection: this.removedLabels,
      finishButtonCallback: this.removeLabels.bind(this)
    });  
  }

  removeLabels(out: Array<IPortfolioCustomLabel>) {
    out.forEach((label: IPortfolioCustomLabel) => {
      label.isNew = false;
      label.isUiDeleted = true;
    });

    let deletedIds = out.map((label: IPortfolioCustomLabel) => label.id);
    let existingIds: Array<string> = (this.data.labels) ? this.data.labels.map((x: IPortfolioCustomLabel) => x.id) : [];

    this.data.labels.forEach((label: IPortfolioCustomLabel) => {
        label.isUiDeleted = deletedIds.includes(label.id);
    });

    out = out.filter((label: IPortfolioCustomLabel) => !existingIds.includes(label.id));
    this.data.labels = (this.data.labels) ? this.data.labels.concat(out) : [].concat(out);

    this.currentLabels = (!this.multipleporfolios) ? this.data.labels.filter((label: IPortfolioCustomLabel) => !label.isUiDeleted) : [];
    this.removedLabels = this.data.labels.filter((label: IPortfolioCustomLabel) => label.isUiDeleted);

    this.util.sortByKey(this.currentLabels, "name", "asc");
    this.util.sortByKey(this.removedLabels, "name", "asc");

    this.change.emit(true);
  }

  getResponse(apiResponse:any){
        
        if(this.isPortfolioAccountService){
            return apiResponse.data ? apiResponse = apiResponse.data.irebalResponse : apiResponse;
        }else{
            return apiResponse;
        }
  }
}
